// Copyright 2025, University of Maryland, All Rights Reserved
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { WorkspacesComponentProps } from './types';
import { AppConfigProvider } from './context/AppConfigContext';
// import { logger } from './utils/logger';
import { GlobalAdminView } from './components/views/GlobalAdminView';
import { WorkspaceAdminView } from './components/views/WorkspaceAdminView';
import { WorkspaceUserView } from './components/views/WorkspaceUserView';

// Create a client - exported for cache invalidation from parent components
export const workspacesQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Main Workspaces Component
 * Uses the globally initialized API instance
 */
export const WorkspacesComponent: React.FC<WorkspacesComponentProps> = ({
  app_id,
  viewType = 'user',
  selectedOrganizationId,
  selectedWorkspaceId,
  debug = false,
  onOrganizationSelect,
  onWorkspaceSelect,
  onWorkspaceExport,
  onWorkspaceImportNew,
  onWorkspaceClone,
  organizationApi,
  workspaceApi,
}) => {
  console.log(`[WorkspacesComponent] Using global API instance for app: ${app_id}`);

  console.log(debug, 'Initializing with:', {
    app_id,
    viewType
  });

  if (!app_id) {
    throw new Error('Application ID is required. Please provide app_id prop.');
  }

  // Handle navigation from breadcrumbs
  const handleNavigate = React.useCallback((targetOrganizationId?: string, targetWorkspaceId?: string) => {
    console.log(debug, 'Breadcrumb navigation:', { targetOrganizationId, targetWorkspaceId });
    
    if (!targetOrganizationId) {
      // Navigate to organizations list (no org selected)
      onOrganizationSelect?.('');
    } else if (!targetWorkspaceId) {
      // Navigate to workspaces list for an organization
      onOrganizationSelect?.(targetOrganizationId);
    } else {
      // Navigate to specific workspace
      onWorkspaceSelect?.(targetOrganizationId, targetWorkspaceId);
    }
  }, [debug, onOrganizationSelect, onWorkspaceSelect]);

  return (
    <QueryClientProvider client={workspacesQueryClient}>
      <AppConfigProvider app_id={app_id} debug={debug}>
        <div className="workspaces-component">
          {/* Render different views based on viewType */}
          {viewType === 'global-admin' && (
            <GlobalAdminView
              selectedOrganizationId={selectedOrganizationId}
              selectedWorkspaceId={selectedWorkspaceId}
              onNavigate={handleNavigate}
              onWorkspaceExport={onWorkspaceExport}
              onWorkspaceImportNew={onWorkspaceImportNew}
              onWorkspaceClone={onWorkspaceClone}
              organizationApi={organizationApi}
              workspaceApi={workspaceApi}
            />
          )}

        {viewType === 'workspace-admin' && (
          <WorkspaceAdminView
            app_id={app_id}
            selectedOrganizationId={selectedOrganizationId}
            selectedWorkspaceId={selectedWorkspaceId}
            onWorkspaceSelect={onWorkspaceSelect}
            debug={debug}
            organizationApi={organizationApi}
            workspaceApi={workspaceApi}
          />
        )}

        {viewType === 'user' && (
          selectedWorkspaceId ? (
            <WorkspaceUserView
              app_id={app_id}
              selectedOrganizationId={selectedOrganizationId!}
              selectedWorkspaceId={selectedWorkspaceId}
              organizationApi={organizationApi}
              workspaceApi={workspaceApi}
            />
          ) : (
            <div className="text-center py-8">Please select a workspace to view</div>
          )
        )}
        </div>
      </AppConfigProvider>
    </QueryClientProvider>
  );
};